import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const API_URL = '/api/auth/';
const USERS_API_URL = '/api/users/';
const PROFILES_API_URL = '/api/access-profiles';
const MODULES_API_URL = '/api/modules/';
const ACTIVITY_API_URL = '/api/activity/';
const HOMEPAGE_API_URL = '/api/homepage';

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  user?: {
    id: number;
    username: string;
    email: string;
    role: string;
  };
  token?: string;
  refreshToken?: string;
}

export interface UserFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: string;
  moduleAccess: { [key: string]: boolean };
  changePassword?: boolean;
  profileId?: number | null;
}

export interface AccessProfile {
  id: number;
  name: string;
  description: string;
  color?: string;
  moduleAccess: Record<string, any>;
  userIds?: number[];
  memberCount: number;
}

export interface AccessProfileResponse {
  success: boolean;
  message?: string;
  profiles?: AccessProfile[];
  profile?: AccessProfile;
  updatedUsers?: number;
}

export interface ApiResponse {
  success: boolean;
  message?: string;
  users?: any[];
  modules?: any[];
  user?: any;
}

export interface OnlineUser {
  userId: number;
  username: string;
  displayName: string;
  role: string;
  app: string;
  ip: string;
  lastSeen: string;
}

export interface ActivityEvent {
  id: number;
  userId: number;
  username: string;
  displayName: string;
  role: string;
  app: string;
  action: string;
  ip: string;
  createdAt: string;
}

export interface OnlineResponse {
  success: boolean;
  windowMs?: number;
  count?: number;
  online?: OnlineUser[];
  message?: string;
}

export interface HistoryResponse {
  success: boolean;
  count?: number;
  events?: ActivityEvent[];
  message?: string;
}

export interface HomepageCategory {
  id: string;
  name: string;
  moduleIds: string[];
}

export interface HomepageResponse {
  success: boolean;
  categories?: HomepageCategory[];
  message?: string;
}

class AuthService {
  private api: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: any) => void;
  }> = [];

  constructor() {
    this.api = axios.create();

    this.api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = this.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('refresh-token') &&
          !originalRequest.url?.includes('login')
        ) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.api(originalRequest);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshed = await this.refreshToken();
            if (refreshed) {
              const newToken = this.getToken()!;
              this.processQueue(null, newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.api(originalRequest);
            } else {
              this.processQueue(new Error('Refresh failed'), '');
              this.forceLogout();
              return Promise.reject(error);
            }
          } catch (refreshError) {
            this.processQueue(refreshError, '');
            this.forceLogout();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: any, token: string) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private forceLogout() {
    this.logout();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await axios.post(API_URL + 'register', data);
    if (response.data.success && response.data.token) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    return response.data;
  }

  async login(data: LoginData): Promise<AuthResponse> {
    const response = await axios.post(API_URL + 'login', data);
    if (response.data.success && response.data.token) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    return response.data;
  }

  logout(): void {
    // Signale la déconnexion au moniteur d'activité (best-effort, non bloquant)
    const token = this.getToken();
    if (token) {
      try {
        fetch(ACTIVITY_API_URL + 'logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ app: 'dashboard' }),
          keepalive: true,
        }).catch(() => {});
      } catch (e) {
        /* silencieux */
      }
    }
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }

  getCurrentUser(): any {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    try {
      const response = await axios.post(API_URL + 'refresh-token', { refreshToken });
      if (response.data.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        return true;
      }
      return false;
    } catch (error) {
      this.logout();
      return false;
    }
  }

  async getUsers(): Promise<ApiResponse> {
    const response = await this.api.get(USERS_API_URL);
    return response.data;
  }

  async createUser(userData: UserFormData): Promise<ApiResponse> {
    const response = await this.api.post(USERS_API_URL, userData);
    return response.data;
  }

  async updateUser(userId: number, userData: Partial<UserFormData>): Promise<ApiResponse> {
    const response = await this.api.put(`${USERS_API_URL}${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: number): Promise<ApiResponse> {
    const response = await this.api.delete(`${USERS_API_URL}${userId}`);
    return response.data;
  }

  async toggleUser(userId: number, active: boolean): Promise<ApiResponse> {
    const response = await this.api.patch(`${USERS_API_URL}${userId}/toggle`, { active });
    return response.data;
  }

  async getAccessProfiles(): Promise<AccessProfileResponse> {
    const response = await this.api.get(PROFILES_API_URL);
    return response.data;
  }

  async createAccessProfile(data: { name: string; description?: string; color?: string; moduleAccess: Record<string, any>; userIds?: number[] }): Promise<AccessProfileResponse> {
    const response = await this.api.post(PROFILES_API_URL, data);
    return response.data;
  }

  async updateAccessProfile(id: number, data: { name: string; description?: string; color?: string; moduleAccess: Record<string, any>; userIds?: number[] }): Promise<AccessProfileResponse> {
    const response = await this.api.put(`${PROFILES_API_URL}/${id}`, data);
    return response.data;
  }

  async deleteAccessProfile(id: number): Promise<AccessProfileResponse> {
    const response = await this.api.delete(`${PROFILES_API_URL}/${id}`);
    return response.data;
  }

  async getModules(): Promise<ApiResponse> {
    const response = await this.api.get(MODULES_API_URL);
    return response.data;
  }

  async getHomepageLayout(): Promise<HomepageResponse> {
    const response = await this.api.get(HOMEPAGE_API_URL);
    return response.data;
  }

  async saveHomepageLayout(categories: HomepageCategory[]): Promise<HomepageResponse> {
    const response = await this.api.put(HOMEPAGE_API_URL, { categories });
    return response.data;
  }

  // === Moniteur d'activité (admin) ===
  async getOnlineUsers(): Promise<OnlineResponse> {
    const response = await this.api.get(ACTIVITY_API_URL + 'online');
    return response.data;
  }

  async getActivityHistory(params?: {
    limit?: number;
    userId?: number;
    app?: string;
    action?: string;
  }): Promise<HistoryResponse> {
    const response = await this.api.get(ACTIVITY_API_URL + 'history', { params });
    return response.data;
  }

  // Jeton SSO court pour l'admin du Live hébergé sur l'Ubuntu (live.zitata.tv)
  async getLiveToken(): Promise<{ success: boolean; token?: string; view?: boolean; admin?: boolean; message?: string }> {
    const response = await this.api.get('/api/live-token');
    return response.data;
  }

  async forgotPassword(email: string): Promise<ApiResponse> {
    const response = await axios.post(API_URL + 'forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, password: string): Promise<ApiResponse> {
    const response = await axios.post(API_URL + 'reset-password', { token, password });
    return response.data;
  }
}

const authService = new AuthService();
export default authService;
